import { jsxs, jsx } from 'react/jsx-runtime';
import * as React from 'react';
import React__default, { useState, useCallback, useEffect } from 'react';
import warning from 'tiny-warning';
import deepmerge from 'deepmerge';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import isObject from 'lodash.isobject';
import isPlainObject from 'lodash.isplainobject';
import isEqual from 'react-fast-compare';
import { string, date, number, boolean, array, object } from 'yup';
import clone from 'lodash.clone';
import toPath from 'lodash.topath';
import cloneDeep from 'lodash.clonedeep';

const FormalizrContext = React__default.createContext(undefined);
FormalizrContext.displayName = 'FormalizrContext';
const FormalizrProvider = FormalizrContext.Provider;
const FormalizrConsumer = FormalizrContext.Consumer;
const useFormalizrContext = () => {
    const formalizr = React__default.useContext(FormalizrContext);
    warning(!!formalizr, 'Formalizr is undefined, Please check if you are calling useFormalizrContext() as a child of a <Formalizr /> component');
    return formalizr;
};

const DEFAULT_VALIDATION_MESSAGES = {
    email: { validity: 'Must be a valid email' },
};

const getErrorMessage = (localeTranslations, layout, errorType, defaultErrorMessage = '', variable) => {
    const validationErrorMessages = layout.widget?.options?.validations;
    if (!validationErrorMessages) {
        return defaultErrorMessage;
    }
    const errorConfig = validationErrorMessages[errorType];
    if (!errorConfig) {
        return defaultErrorMessage;
    }
    if (errorConfig.label) {
        if (variable && errorConfig.label.includes('{')) {
            const firstIndex = errorConfig.label.indexOf('{');
            const lastIndex = errorConfig.label.lastIndexOf('}');
            const firstPart = errorConfig.label.slice(0, firstIndex);
            const lastPart = errorConfig.label.slice(lastIndex + 1);
            return `${firstPart}${variable}${lastPart}`;
        }
        return errorConfig.label;
    }
    const i18nKey = errorConfig.i18n;
    if (!i18nKey) {
        return defaultErrorMessage;
    }
    if (variable &&
        localeTranslations[i18nKey] &&
        localeTranslations[i18nKey]?.includes('{')) {
        const firstIndex = localeTranslations[i18nKey]?.indexOf('{');
        const lastIndex = localeTranslations[i18nKey]?.lastIndexOf('}');
        const firstPart = localeTranslations[i18nKey]?.slice(0, firstIndex);
        const lastPart = localeTranslations[i18nKey]?.slice(lastIndex + 1);
        return `${firstPart}${variable}${lastPart}`;
    }
    return localeTranslations[i18nKey];
};
const createStringValidation = (element, localeTranslations, defaultErrorMessage) => {
    const { data, layout } = element;
    let validator;
    validator = string();
    if (data?.format === 'date') {
        validator = date();
    }
    if (data?.format === 'email') {
        validator = validator.email(getErrorMessage(localeTranslations, layout, 'email', DEFAULT_VALIDATION_MESSAGES.email.validity));
    }
    if (data?.minLength) {
        validator = validator.min(data.minLength, getErrorMessage(localeTranslations, layout, 'min', defaultErrorMessage, data.minLength));
    }
    if (data?.maxLength) {
        validator = validator.max(data.maxLength, getErrorMessage(localeTranslations, layout, 'max', defaultErrorMessage, data.maxLength));
    }
    if (data?.pattern) {
        validator = validator.matches(data.pattern);
    }
    return validator;
};
const createNumberValidation = (element, localeTranslations, defaultErrorMessage) => {
    const { data, layout } = element;
    let validator = number();
    if (data?.minimum) {
        validator = validator.min(data.minimum, getErrorMessage(localeTranslations, layout, 'minimum', defaultErrorMessage, data.minimum));
    }
    if (data?.maximum) {
        validator = validator.max(data.maximum, getErrorMessage(localeTranslations, layout, 'maximum', defaultErrorMessage, data.minimum));
    }
    if (data?.multipleOf) {
        const errorMessage = getErrorMessage(localeTranslations, layout, 'multipleOf', defaultErrorMessage, data.multipleOf);
        validator = validator.test('is-multiple-of', errorMessage, value => {
            if (!value) {
                return true;
            }
            return value % data.multipleOf === 0;
        });
    }
    return validator;
};
const createIntegerValidation = (element, localeTranslations, defaultErrorMessage) => {
    const schema = createNumberValidation(element, localeTranslations, defaultErrorMessage).integer();
    return schema;
};
const createBooleanValidation = () => {
    const schema = boolean();
    return schema;
};
const createArrayValidation = (element, localeTranslations, defaultErrorMessage) => {
    const { data, layout } = element;
    let validator = array();
    if (data?.minItems) {
        validator = validator.min(data.minItems, getErrorMessage(localeTranslations, layout, 'minItems', defaultErrorMessage, data.minLength));
    }
    if (data?.maxItems) {
        validator = validator.max(data.maxItems, getErrorMessage(localeTranslations, layout, 'maxItems', defaultErrorMessage, data.minLength));
    }
    return validator;
};

const createValidationSchema = (getUsedDataElements, schema, locale, defaultErrorMessage = '') => {
    const translations = schema.output.translations;
    const uiSchema = schema.output.ui;
    const data = schema.output.data;
    const layoutElements = getUsedDataElements(data, uiSchema.elements);
    const localeTranslations = translations[locale];
    const { required } = data;
    const validationSchema = {};
    for (const [key, value] of Object.entries(layoutElements)) {
        const { type } = value.data;
        let validator;
        if (type === 'string') {
            validator = createStringValidation(value, localeTranslations, defaultErrorMessage);
        }
        if (type === 'number') {
            validator = createNumberValidation(value, localeTranslations, defaultErrorMessage);
        }
        if (type === 'integer') {
            validator = createIntegerValidation(value, localeTranslations, defaultErrorMessage);
        }
        if (type === 'boolean') {
            validator = createBooleanValidation();
        }
        if (type === 'array') {
            validator = createArrayValidation(value, localeTranslations, defaultErrorMessage);
        }
        if (type === 'boolean') {
            validationSchema[key] = required.includes(key)
                ? validator.oneOf([true], getErrorMessage(localeTranslations, value.layout, 'required', defaultErrorMessage))
                : validator;
        }
        else {
            validationSchema[key] = required.includes(key)
                ? validator.required(getErrorMessage(localeTranslations, value.layout, 'required', defaultErrorMessage))
                : validator;
        }
    }
    return object().shape(validationSchema);
};
const checkRule = (rule, value) => {
    const { condition: { schema }, } = rule;
    if ('not' in schema) {
        return !validateRule(rule, value);
    }
    return validateRule(rule, value);
};
const validateRule = (rule, value) => {
    const { condition: { schema }, } = rule;
    if ('const' in schema) {
        return value === schema.const;
    }
    if ('enum' in schema) {
        return schema.enum.includes(value);
    }
    return false;
};

const getUsedDataElements = (dataSchema, uiSchemaElements, layoutElements = []) => {
    const arrayElements = loopThroughLayoutElements(dataSchema, uiSchemaElements, layoutElements);
    const elements = arrayElements.reduce((acc, curr) => {
        const obj = Object.entries(curr);
        if (obj[0]) {
            const [key, val] = obj[0];
            if (key && val) {
                return {
                    ...acc,
                    [key]: val,
                };
            }
        }
        return acc;
    }, {});
    return elements;
};
const loopThroughLayoutElements = (dataSchema, uiSchemaElements, _layoutElements = []) => {
    const layoutElements = [..._layoutElements];
    const mapped = uiSchemaElements
        .map(element => {
        const { type } = element;
        if (type === 'ContentfulEntry') {
            // Return empty array if the element is a reference
            // It gets filtered out in the next step
            return [];
        }
        if (type === 'Categorization') {
            return [
                ...layoutElements,
                ...loopThroughLayoutElements(dataSchema, element.elements, layoutElements),
            ];
        }
        if (type === 'Category') {
            return [
                ...layoutElements,
                ...loopThroughLayoutElements(dataSchema, element.elements, layoutElements),
            ];
        }
        if (type === 'VerticalLayout') {
            return [
                ...layoutElements,
                ...loopThroughLayoutElements(dataSchema, element.elements, layoutElements),
            ];
        }
        if (type === 'HorizontalLayout') {
            return [
                ...layoutElements,
                ...loopThroughLayoutElements(dataSchema, element.elements, layoutElements),
            ];
        }
        if (type === 'Group') {
            return [
                ...layoutElements,
                ...loopThroughLayoutElements(dataSchema, element.elements, layoutElements),
            ];
        }
        if (type === 'Control') {
            // Find the corresponding data schema element
            const filtered = Object.entries(dataSchema.properties)
                .map(([key, value]) => {
                if (element.scope.endsWith(key)) {
                    return [
                        {
                            ...layoutElements,
                            [key]: {
                                data: { ...value },
                                layout: { ...element },
                            },
                        },
                    ];
                }
                return {};
            })
                .filter(value => Object.keys(value).length > 0);
            return [...new Set(filtered)];
        }
    })
        .flat(Infinity);
    return [...new Set(mapped)];
};
const createRuleSchema = (elements) => {
    const layoutElements = getLayoutElements(elements);
    const ruleSchema = layoutElements.reduce((acc, curr) => {
        if (curr?.rule) {
            const key = curr.rule.condition.scope.split('/').pop();
            const obj = {
                condition: curr.rule.condition,
                effect: curr.rule.effect,
                isValid: false,
            };
            return {
                ...acc,
                [key]: obj,
            };
        }
        return acc;
    }, {});
    return ruleSchema;
};
const getLayoutElements = (elements, _layout = []) => {
    const layout = [..._layout];
    const mapped = elements
        .map(el => {
        const { type } = el;
        if (type === 'Categorization') {
            return [...layout, el, ...getLayoutElements(el.elements, layout)];
        }
        if (type === 'Category') {
            return [...layout, el, ...getLayoutElements(el.elements, layout)];
        }
        if (type === 'VerticalLayout') {
            return [...layout, el, ...getLayoutElements(el.elements, layout)];
        }
        if (type === 'HorizontalLayout') {
            return [...layout, el, ...getLayoutElements(el.elements, layout)];
        }
        if (type === 'Group') {
            return [...layout, el, ...getLayoutElements(el.elements, layout)];
        }
    })
        .filter(el => el !== undefined)
        .flat(Infinity);
    return mapped;
};
const addRuleValidation = (el, rules, values) => {
    if (el.rule) {
        const key = el.rule.condition.scope.split('/').pop();
        if (key !== undefined) {
            const rule = rules[key];
            if (rule) {
                const isValid = checkRule(el.rule, values[key]);
                return {
                    ...el,
                    rule: {
                        ...el.rule,
                        isValid,
                    },
                };
            }
        }
    }
    return el;
};
const filterOutValidRules = (el) => {
    if (el?.rule) {
        return el.rule?.isValid;
    }
    return true;
};
const rebuildUiSchema = (uiSchema, rules, values, _layoutElements = []) => {
    const layout = [..._layoutElements];
    const mapped = uiSchema
        .map(el => {
        const { type } = el;
        if (type === 'ContentfulEntry') {
            return el;
        }
        if (type === 'Categorization' || type === 'Category') {
            const children = rebuildUiSchema(el.elements, rules, values).filter(filterOutValidRules);
            return [
                ...layout,
                {
                    ...addRuleValidation(el, rules, values),
                    elements: children,
                },
            ];
        }
        if (type === 'HorizontalLayout' ||
            type === 'VerticalLayout' ||
            type === 'Group') {
            const children = rebuildUiSchema(el.elements, rules, values).filter(filterOutValidRules);
            return [
                ...layout,
                {
                    ...addRuleValidation(el, rules, values),
                    elements: children,
                },
            ];
        }
        if (type === 'Control') {
            return el;
        }
    })
        .filter(el => el !== undefined)
        .flat();
    return mapped;
};

const typeValueMap = {
    array: [],
    boolean: false,
    email: '',
    number: 0,
    object: {},
    string: '',
};
const createInitialValues = (passedValues, dataSchema, uiSchema) => {
    const elements = getUsedDataElements(dataSchema, uiSchema.elements);
    return Object.entries(elements).reduce((acc, [key, value]) => {
        let initialValue = passedValues?.[key] ?? value.data.default;
        if (initialValue === undefined) {
            initialValue = typeValueMap[value.data.type];
        }
        return {
            ...acc,
            [key]: initialValue,
        };
    }, {});
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/** @private is the given object an integer? */
const isInteger = (obj) => String(Math.floor(Number(obj))) === obj;
/** @private is the given object a Function? */
// eslint-disable-next-line @typescript-eslint/ban-types
const isFunction = (obj) => typeof obj === 'function';
/** @private is the given object a string? */
const isString = (obj) => Object.prototype.toString.call(obj) === '[object String]';
/** @private is the given object/value a promise? */
const isPromise = (value) => isObject(value) && isFunction(value.then);
// /**
//  * Deeply set a value from in object via it's path. If the value at `path`
//  * has changed, return a shallow copy of obj with `value` set at `path`.
//  * If `value` has not changed, return the original `obj`.
//  *
//  * Existing objects / arrays along `path` are also shallow copied. Sibling
//  * objects along path retain the same internal js reference. Since new
//  * objects / arrays are only created along `path`, we can test if anything
//  * changed in a nested structure by comparing the object's reference in
//  * the old and new object, similar to how russian doll cache invalidation
//  * works.
//  *
//  * In earlier versions of this function, which used cloneDeep, there were
//  * issues whereby settings a nested value would mutate the parent
//  * instead of creating a new object. `clone` avoids that bug making a
//  * shallow copy of the objects along the update path
//  * so no object is mutated in place.
//  *
//  * Before changing this function, please read through the following
//  * discussions.
//  *
//  * @see https://github.com/developit/linkstate
//  * @see https://github.com/jaredpalmer/formik/pull/123
//  */
function setIn(obj, path, value) {
    const res = clone(obj); // this keeps inheritance when obj is a class
    let resVal = res;
    let i = 0;
    const pathArray = toPath(path);
    for (; i < pathArray.length - 1; i++) {
        const currentPath = pathArray[i];
        const currentObj = getIn(obj, pathArray.slice(0, i + 1));
        if (currentObj && (isObject(currentObj) || Array.isArray(currentObj))) {
            resVal = resVal[currentPath] = clone(currentObj);
        }
        else {
            const nextPath = pathArray[i + 1];
            resVal = resVal[currentPath] =
                isInteger(nextPath) && Number(nextPath) >= 0 ? [] : {};
        }
    }
    // Return original object if new value is the same as current
    if ((i === 0 ? obj : resVal)[pathArray[i]] === value) {
        return obj;
    }
    if (value === undefined) {
        delete resVal[pathArray[i]];
    }
    else {
        resVal[pathArray[i]] = value;
    }
    // If the path array has a single element, the loop did not run.
    // Deleting on `resVal` had no effect in this scenario, so we delete on the result instead.
    if (i === 0 && value === undefined) {
        delete res[pathArray[i]];
    }
    return res;
}
// /**
//  * Deeply get a value from an object via its path.
//  */
function getIn(obj, key, def, p = 0) {
    const path = toPath(key);
    while (obj && p < path.length) {
        // eslint-disable-next-line no-param-reassign
        obj = obj[path[p++]];
    }
    // check if path is not in the end
    if (p !== path.length && !obj) {
        return def;
    }
    return obj === undefined ? def : obj;
}
// /**
//  * Recursively a set the same value for all keys and arrays nested object, cloning
//  * @param object
//  * @param value
//  * @param visited
//  * @param response
//  */
function setNestedObjectValues(object, value, visited = new WeakMap(), response = {}) {
    for (const k of Object.keys(object)) {
        const val = object[k];
        if (isObject(val)) {
            if (!visited.get(val)) {
                visited.set(val, true);
                // In order to keep array values consistent for both dot path  and
                // bracket syntax, we need to check if this is an array so that
                // this will output  { friends: [true] } and not { friends: { "0": true } }
                response[k] = Array.isArray(val) ? [] : {};
                setNestedObjectValues(val, value, visited, response[k]);
            }
        }
        else {
            response[k] = value;
        }
    }
    return response;
}

// React currently throws a warning when using useLayoutEffect on the server.
// To get around it, we can conditionally useEffect on the server (no-op) and
// useLayoutEffect in the browser.
// @see https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85
const useIsomorphicLayoutEffect = typeof window !== 'undefined' &&
    typeof window.document !== 'undefined' &&
    typeof window.document.createElement !== 'undefined'
    ? React.useLayoutEffect
    : React.useEffect;
function useEventCallback(fn) {
    const ref = React.useRef(fn);
    // we copy a ref to the callback scoped to the current state/props on each render
    useIsomorphicLayoutEffect(() => {
        ref.current = fn;
    });
    return React.useCallback((...args) => ref.current.apply(void 0, args), []);
}

const formalizrReducer = (state, msg) => {
    switch (msg.type) {
        case 'SET_VALUES':
            return { ...state, values: msg.payload };
        case 'SET_TOUCHED':
            return { ...state, touched: msg.payload };
        case 'SET_ERRORS':
            if (isEqual(state.errors, msg.payload)) {
                return state;
            }
            return { ...state, errors: msg.payload };
        case 'SET_STATUS':
            return { ...state, status: msg.payload };
        case 'SET_ISSUBMITTING':
            return { ...state, isSubmitting: msg.payload };
        case 'SET_ISVALIDATING':
            return { ...state, isValidating: msg.payload };
        case 'SET_FIELD_VALUE':
            return {
                ...state,
                values: {
                    ...state.values,
                    [msg.payload.field]: msg.payload.value,
                },
            };
        case 'SET_FIELD_TOUCHED':
            return {
                ...state,
                touched: {
                    ...state.touched,
                    [msg.payload.field]: msg.payload.value,
                },
            };
        case 'SET_FIELD_ERROR':
            return {
                ...state,
                // errors: setIn(state.errors, msg.payload.field, msg.payload.value),
                errors: {
                    ...state.errors,
                    [msg.payload.field]: msg.payload.value,
                },
            };
        case 'RESET_FORM':
            return { ...state, ...msg.payload };
        case 'SET_FORMIK_STATE':
            return msg.payload(state);
        case 'SUBMIT_ATTEMPT':
            return {
                ...state,
                isSubmitting: true,
                submitCount: state.submitCount + 1,
                touched: setNestedObjectValues(state.values, true),
            };
        case 'SUBMIT_FAILURE':
            return {
                ...state,
                isSubmitting: false,
            };
        case 'SUBMIT_SUCCESS':
            return {
                ...state,
                isSubmitting: false,
            };
        case 'SET_UI_SCHEMA':
            return {
                ...state,
                uiSchema: msg.payload,
            };
        case 'SET_UI_SCHEMA_CURRENT':
            return {
                ...state,
                uiSchemaCurrent: msg.payload,
            };
        case 'SET_VALIDATION_SCHEMA':
            return {
                ...state,
                validationSchema: msg.payload,
            };
        default:
            return state;
    }
};
// Initial empty states // objects
const emptyErrors = {};
const emptyTouched = {};
const emptyValidationSchema = {};
const useFormalizrState = (props) => {
    const { schema } = props;
    const uiSchema = get(schema, 'output.ui');
    const dataSchema = get(schema, 'output.data');
    const generatedInitialValues = createInitialValues(props.initialValues, dataSchema, uiSchema);
    const initialValues = React__default.useRef(generatedInitialValues);
    const initialErrors = React__default.useRef(props.initialErrors || emptyErrors);
    const initialTouched = React__default.useRef(props.initialTouched || emptyTouched);
    const initialStatus = React__default.useRef(props.initialStatus);
    const [, setIteration] = React__default.useState(0);
    const stateRef = React__default.useRef({
        errors: props.initialErrors || emptyErrors,
        initialUiSchema: props.schema.output.ui,
        isSubmitting: false,
        isValidating: false,
        status: props.initialStatus,
        submitCount: 0,
        touched: props.initialTouched || emptyTouched,
        uiSchema,
        uiSchemaCurrent: uiSchema,
        validationSchema: emptyValidationSchema,
        values: generatedInitialValues,
    });
    const state = stateRef.current;
    const dispatch = React__default.useCallback(action => {
        const prev = stateRef.current;
        stateRef.current = formalizrReducer(prev, action);
        // force rerender
        if (prev !== stateRef.current)
            setIteration(x => x + 1);
    }, []);
    return [
        state,
        dispatch,
        {
            initialErrors,
            initialStatus,
            initialTouched,
            initialValues,
        },
    ];
};

const useWizard = (initialData, initialStep = 1) => {
    // If the initial data is not a Categorization, return the initial data and step is always 0
    if (initialData.elements[0]?.type !== 'Categorization') {
        return [initialData, initialStep, 1];
    }
    const numberOfSteps = initialData.elements[0].elements.length;
    const [data, setData] = useState(initialData);
    const [currentStep, setCurrentStep] = useState(initialStep);
    const goToNextStep = useCallback(() => {
        setCurrentStep(prevStep => prevStep === numberOfSteps ? prevStep : prevStep + 1);
    }, []);
    const goToPreviousStep = useCallback(() => {
        setCurrentStep(prevStep => (prevStep === 0 ? prevStep : prevStep - 1));
    }, []);
    useEffect(() => {
        if (initialData.elements[0]?.type === 'Categorization') {
            const current = (currentStep === numberOfSteps ? numberOfSteps : currentStep) - 1;
            const layoutElement = initialData.elements[0]?.elements[current];
            if (layoutElement) {
                const updatedData = overwriteCategorizationElements(initialData, layoutElement);
                setData(updatedData);
            }
        }
    }, [currentStep]);
    return [
        data,
        currentStep,
        numberOfSteps,
        goToNextStep,
        goToPreviousStep,
    ];
};
const overwriteCategorizationElements = (obj, newElements) => {
    const clonedObj = cloneDeep(obj);
    if (clonedObj.elements[0] &&
        clonedObj.elements[0]?.type === 'Categorization') {
        clonedObj.elements[0].elements = [newElements];
    }
    return clonedObj;
};

const enrichTranslationsWithFieldLabels = (uiSchema, translations, locale) => {
    const getName = (element) => {
        if (!('scope' in element)) {
            if ('i18n' in element && element.i18n) {
                return element.i18n;
            }
            if ('label' in element && element.label) {
                return element.label;
            }
            return 'ELEMENT_WITHOUT_NAME_I18N_OR_LABEL';
        }
        const lastIndex = element.scope?.lastIndexOf('/');
        const name = element.scope?.slice(lastIndex + 1);
        return (name ??
            element.i18n ??
            ('label' in element && element.label
                ? element.label
                : 'ELEMENT_WITHOUT_NAME_I18N_OR_LABEL'));
    };
    const getFieldsWithLabels = (elements) => {
        const fieldsWithLabels = new Map();
        elements.forEach(element => {
            if ('label' in element && typeof element.label === 'string') {
                fieldsWithLabels.set(getName(element), element.label);
            }
            if ('elements' in element && element.elements) {
                const innerFieldsWithLabels = getFieldsWithLabels(element.elements);
                innerFieldsWithLabels.forEach((value, key) => {
                    fieldsWithLabels.set(key, value);
                });
            }
        });
        return fieldsWithLabels;
    };
    const fieldsWithLabels = getFieldsWithLabels(uiSchema.elements);
    const enrichedTranslations = { ...translations };
    fieldsWithLabels.forEach((value, key) => {
        const localeTranslations = enrichedTranslations[locale] ?? (enrichedTranslations[locale] = {});
        localeTranslations[key] = value;
    });
    return enrichedTranslations;
};
const useFormalizr = (props) => {
    const { schema, onSubmit, validateOnBlur, validateOnChange, validateOnMount, locale, defaultErrorMessage, disableButtonsOnErrors, } = props;
    const uiSchema = get(schema, 'output.ui');
    const dataSchema = get(schema, 'output.data');
    const translations = enrichTranslationsWithFieldLabels(uiSchema, get(schema, 'output.translations') ?? {}, locale);
    const [state, dispatch, { initialErrors, initialStatus, initialTouched, initialValues },] = useFormalizrState({
        initialErrors: props.initialErrors,
        initialStatus: props.initialStatus,
        initialTouched: props.initialTouched,
        initialValues: props.initialValues,
        schema,
    });
    const [currentPage, currentStep, numberOfSteps, _goToNextStep, _goToPreviousStep,] = useWizard(state.uiSchema);
    const ui = currentPage ? currentPage : uiSchema;
    const validationSchema = createValidationSchema(getUsedDataElements, {
        ...schema,
        output: {
            ...schema.output,
            ui,
        },
    }, locale, defaultErrorMessage);
    const rules = createRuleSchema(uiSchema.elements);
    const isMounted = React.useRef(false);
    const fieldRegistry = React.useRef({});
    React.useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    });
    React.useEffect(() => {
        // Set the UI schema with updated values based on the rules
        if (state.values) {
            const currentElements = rebuildUiSchema(state.uiSchemaCurrent.elements, 
            // uiSchema.elements,
            rules, state.values);
            const completeElements = rebuildUiSchema(
            // state.uiSchemaCurrent.elements,
            uiSchema.elements, rules, state.values);
            dispatch({
                payload: {
                    ...uiSchema,
                    elements: currentElements,
                },
                type: 'SET_UI_SCHEMA_CURRENT',
            });
            dispatch({
                payload: {
                    ...uiSchema,
                    elements: completeElements,
                },
                type: 'SET_UI_SCHEMA',
            });
        }
        return () => {
            dispatch({ payload: uiSchema, type: 'SET_UI_SCHEMA_CURRENT' });
        };
    }, [state.values]);
    React.useEffect(() => {
        // Set the UI schema with the current step elements
        if (currentPage) {
            dispatch({
                payload: currentPage,
                type: 'SET_UI_SCHEMA_CURRENT',
            });
        }
        return () => {
            dispatch({ payload: uiSchema, type: 'SET_UI_SCHEMA_CURRENT' });
        };
    }, [currentPage]);
    React.useEffect(() => {
        if (state.values) {
            const updatedValidationSchema = createValidationSchema(getUsedDataElements, {
                ...schema,
                output: { ...schema.output, ui: state.uiSchemaCurrent },
            }, locale, defaultErrorMessage);
            dispatch({
                payload: updatedValidationSchema,
                type: 'SET_VALIDATION_SCHEMA',
            });
        }
        return () => {
            dispatch({
                payload: validationSchema,
                type: 'SET_VALIDATION_SCHEMA',
            });
        };
    }, [state.uiSchemaCurrent]);
    // TODO Not necessary I think
    // const runValidateHandler = React.useCallback(
    //   (values: Values, field?: string): Promise<FormalizrErrors<Values>> => {
    //     return new Promise((resolve, reject) => {
    //       const maybePromisedErrors = (props.validate as any)(values, field);
    //       if (maybePromisedErrors == null) {
    //         // use loose null check here on purpose
    //         resolve({});
    //       } else if (isPromise(maybePromisedErrors)) {
    //         (maybePromisedErrors as Promise<any>).then(
    //           errors => {
    //             resolve(errors || {});
    //           },
    //           actualException => {
    //             if ("production" !== 'production') {
    //               console.warn(
    //                 `Warning: An unhandled error was caught during validation in <Formik validate />`,
    //                 actualException,
    //               );
    //             }
    //             reject(actualException);
    //           },
    //         );
    //       } else {
    //         resolve(maybePromisedErrors);
    //       }
    //     });
    //   },
    //   [props.validate],
    // );
    /**
     * Run validation against a Yup schema and optionally run a function if successful
     */
    const runValidationSchema = React.useCallback((values, field) => {
        const schema = isFunction(state.validationSchema)
            ? state.validationSchema(field)
            : state.validationSchema;
        const promise = field && schema.validateAt
            ? schema.validateAt(field, values)
            : validateYupSchema(values, schema);
        return new Promise((resolve, reject) => {
            promise.then(() => {
                resolve({});
            }, (err) => {
                // Yup will throw a validation error if validation fails. We catch those and
                // resolve them into Formalizr errors. We can sniff if something is a Yup error
                // by checking error.name.
                // @see https://github.com/jquense/yup#validationerrorerrors-string--arraystring-value-any-path-string
                if (err.name === 'ValidationError') {
                    resolve(yupToFormErrors(err));
                }
                else {
                    reject(err);
                }
            });
        });
    }, [state.validationSchema]);
    const runSingleFieldLevelValidation = React.useCallback((field, value) => {
        return new Promise(resolve => resolve(fieldRegistry?.current[field]?.validate(value)));
    }, []);
    const runFieldLevelValidations = React.useCallback((values) => {
        const fieldKeysWithValidation = Object.keys(fieldRegistry.current).filter(f => isFunction(fieldRegistry.current[f]?.validate));
        // Construct an array with all of the field validation functions
        const fieldValidations = fieldKeysWithValidation.length > 0
            ? fieldKeysWithValidation.map(f => runSingleFieldLevelValidation(f, getIn(values, f)))
            : [Promise.resolve('DO_NOT_DELETE_YOU_WILL_BE_FIRED')]; // use special case ;)
        return Promise.all(fieldValidations).then((fieldErrorsList) => fieldErrorsList.reduce((prev, curr, index) => {
            if (curr === 'DO_NOT_DELETE_YOU_WILL_BE_FIRED') {
                return prev;
            }
            if (curr) {
                // @ts-expect-error - TODO fix types
                // eslint-disable-next-line no-param-reassign
                prev = setIn(prev, fieldKeysWithValidation[index], curr);
            }
            return prev;
        }, {}));
    }, [runSingleFieldLevelValidation]);
    // Run all validations and return the result
    const runAllValidations = React.useCallback((values) => {
        return Promise.all([
            runFieldLevelValidations(values),
            state.validationSchema ? runValidationSchema(values) : {},
            // props.validate ? runValidateHandler(values) : {},
            {},
        ]).then(([fieldErrors, schemaErrors, validateErrors]) => {
            const combinedErrors = deepmerge.all([fieldErrors, schemaErrors, validateErrors], { arrayMerge });
            return combinedErrors;
        });
    }, [
        // props.validate,
        state.validationSchema,
        runFieldLevelValidations,
        // runValidateHandler,
        runValidationSchema,
    ]);
    // Run all validations methods and update state accordingly
    const validateFormWithHighPriority = useEventCallback((values = state.values) => {
        dispatch({ payload: true, type: 'SET_ISVALIDATING' });
        return runAllValidations(values).then(combinedErrors => {
            if (isMounted.current) {
                dispatch({ payload: false, type: 'SET_ISVALIDATING' });
                dispatch({ payload: combinedErrors, type: 'SET_ERRORS' });
            }
            return combinedErrors;
        });
    });
    React.useEffect(() => {
        if (validateOnMount &&
            isMounted.current === true &&
            !isEmpty(state.validationSchema)) {
            validateFormWithHighPriority();
        }
    }, [validateOnMount, validateFormWithHighPriority, state.validationSchema]);
    const resetForm = React.useCallback((nextState) => {
        const values = nextState && nextState.values
            ? nextState.values
            : initialValues.current;
        const errors = nextState && nextState.errors
            ? nextState.errors
            : initialErrors.current
                ? initialErrors.current
                : props.initialErrors || {};
        const touched = nextState && nextState.touched
            ? nextState.touched
            : initialTouched.current
                ? initialTouched.current
                : props.initialTouched || {};
        const status = nextState && nextState.status
            ? nextState.status
            : initialStatus.current
                ? initialStatus.current
                : props.initialStatus;
        initialValues.current = values;
        initialErrors.current = errors;
        initialTouched.current = touched;
        initialStatus.current = status;
        const dispatchFn = () => {
            dispatch({
                payload: {
                    errors,
                    isSubmitting: !!nextState && !!nextState.isSubmitting,
                    isValidating: !!nextState && !!nextState.isValidating,
                    status,
                    submitCount: !!nextState &&
                        !!nextState.submitCount &&
                        typeof nextState.submitCount === 'number'
                        ? nextState.submitCount
                        : 0,
                    touched,
                    values,
                },
                type: 'RESET_FORM',
            });
        };
        if (props.onReset) {
            const maybePromisedOnReset = props.onReset(state.values, imperativeMethods);
            if (isPromise(maybePromisedOnReset)) {
                maybePromisedOnReset.then(dispatchFn);
            }
            else {
                dispatchFn();
            }
        }
        else {
            dispatchFn();
        }
    }, [
        props.initialErrors,
        props.initialStatus,
        props.initialTouched,
        props.onReset,
    ]);
    const registerField = React.useCallback((name, { validate }) => {
        fieldRegistry.current[name] = {
            validate,
        };
    }, []);
    const unregisterField = React.useCallback((name) => {
        delete fieldRegistry.current[name];
    }, []);
    const setTouched = useEventCallback((touched, shouldValidate) => {
        dispatch({ payload: touched, type: 'SET_TOUCHED' });
        const willValidate = shouldValidate === undefined ? validateOnBlur : shouldValidate;
        return willValidate
            ? validateFormWithHighPriority(state.values)
            : Promise.resolve();
    });
    const setErrors = React.useCallback((errors) => {
        dispatch({ payload: errors, type: 'SET_ERRORS' });
    }, []);
    const setValues = useEventCallback((values, shouldValidate) => {
        const resolvedValues = isFunction(values) ? values(state.values) : values;
        dispatch({ payload: resolvedValues, type: 'SET_VALUES' });
        const willValidate = shouldValidate === undefined ? validateOnChange : shouldValidate;
        return willValidate
            ? validateFormWithHighPriority(resolvedValues)
            : Promise.resolve();
    });
    const setFieldError = React.useCallback((field, value) => {
        dispatch({
            payload: { field, value },
            type: 'SET_FIELD_ERROR',
        });
    }, []);
    const setFieldValue = useEventCallback((field, value, shouldValidate) => {
        dispatch({
            payload: {
                field,
                value,
            },
            type: 'SET_FIELD_VALUE',
        });
        const willValidate = shouldValidate === undefined ? validateOnChange : shouldValidate;
        return willValidate
            ? validateFormWithHighPriority(setIn(state.values, field, value))
            : Promise.resolve();
    });
    const executeChange = React.useCallback((eventOrTextValue, maybePath) => {
        // By default, assume that the first argument is a string. This allows us to use
        // handleChange with React Native and React Native Web's onChangeText prop which
        // provides just the value of the input.
        let field = maybePath;
        let val = eventOrTextValue;
        let parsed;
        // If the first argument is not a string though, it has to be a synthetic React Event (or a fake one),
        // so we handle like we would a normal HTML change event.
        if (!isString(eventOrTextValue)) {
            // If we can, persist the event
            // @see https://reactjs.org/docs/events.html#event-pooling
            if (eventOrTextValue.persist) {
                eventOrTextValue.persist();
            }
            const target = eventOrTextValue.target
                ? eventOrTextValue.target
                : eventOrTextValue.currentTarget;
            const { type, name, id, value, checked, outerHTML, options, multiple } = target;
            field = maybePath ? maybePath : name ? name : id;
            if (!field) {
                warnAboutMissingIdentifier({
                    documentationAnchorLink: 'handlechange-e-reactchangeeventany--void',
                    handlerName: 'handleChange',
                    htmlContent: outerHTML,
                });
            }
            val = /number|range/.test(type)
                ? ((parsed = parseFloat(value)), isNaN(parsed) ? '' : parsed)
                : /checkbox/.test(type) // checkboxes
                    ? getValueForCheckbox(getIn(state.values, field), checked, value)
                    : options && multiple // <select multiple>
                        ? getSelectedValues(options)
                        : value;
        }
        if (field) {
            // Set form fields by name
            setFieldValue(field, val);
        }
    }, [setFieldValue, state.values]);
    const handleChange = useEventCallback((eventOrPath) => {
        if (isString(eventOrPath)) {
            return event => executeChange(event, eventOrPath);
        }
        else {
            executeChange(eventOrPath);
        }
    });
    const setFieldTouched = useEventCallback((field, touched = true, shouldValidate) => {
        dispatch({
            payload: {
                field,
                value: touched,
            },
            type: 'SET_FIELD_TOUCHED',
        });
        const willValidate = shouldValidate === undefined ? validateOnBlur : shouldValidate;
        return willValidate
            ? validateFormWithHighPriority(state.values)
            : Promise.resolve();
    });
    const executeBlur = React.useCallback((e, path) => {
        if (e.persist) {
            e.persist();
        }
        const { name, id } = e.target;
        const field = path ? path : name ? name : id;
        // if (!field && __DEV__) {
        //   warnAboutMissingIdentifier({
        //     documentationAnchorLink: 'handleblur-e-any--void',
        //     handlerName: 'handleBlur',
        //     htmlContent: outerHTML,
        //   });
        // }
        setFieldTouched(field, true);
    }, [setFieldTouched]);
    const handleBlur = useEventCallback((eventOrString) => {
        if (isString(eventOrString)) {
            return event => executeBlur(event, eventOrString);
        }
        else {
            executeBlur(eventOrString);
        }
    });
    const setFormalizrState = React.useCallback((stateOrCb) => {
        if (isFunction(stateOrCb)) {
            dispatch({ payload: stateOrCb, type: 'SET_FORMIK_STATE' });
        }
        else {
            dispatch({
                payload: () => stateOrCb,
                type: 'SET_FORMIK_STATE',
            });
        }
    }, []);
    const setStatus = React.useCallback((status) => {
        dispatch({ payload: status, type: 'SET_STATUS' });
    }, []);
    const setSubmitting = React.useCallback((isSubmitting) => {
        dispatch({ payload: isSubmitting, type: 'SET_ISSUBMITTING' });
    }, []);
    const submitForm = useEventCallback(() => {
        dispatch({ type: 'SUBMIT_ATTEMPT' });
        return validateFormWithHighPriority().then((combinedErrors) => {
            // In case an error was thrown and passed to the resolved Promise,
            // `combinedErrors` can be an instance of an Error. We need to check
            // that and abort the submit.
            // If we don't do that, calling `Object.keys(new Error())` yields an
            // empty array, which causes the validation to pass and the form
            // to be submitted.
            const isInstanceOfError = combinedErrors instanceof Error;
            const isActuallyValid = !isInstanceOfError && Object.keys(combinedErrors).length === 0;
            if (isActuallyValid) {
                // Proceed with submit...
                //
                // To respect sync submit fns, we can't simply wrap executeSubmit in a promise and
                // _always_ dispatch SUBMIT_SUCCESS because isSubmitting would then always be false.
                // This would be fine in simple cases, but make it impossible to disable submit
                // buttons where people use callbacks or promises as side effects (which is basically
                // all of v1 Formik code). Instead, recall that we are inside of a promise chain already,
                //  so we can try/catch executeSubmit(), if it returns undefined, then just bail.
                // If there are errors, throw em. Otherwise, wrap executeSubmit in a promise and handle
                // cleanup of isSubmitting on behalf of the consumer.
                let promiseOrUndefined;
                // eslint-disable-next-line no-useless-catch
                try {
                    promiseOrUndefined = executeSubmit();
                    // Bail if it's sync, consumer is responsible for cleaning up
                    // via setSubmitting(false)
                    if (promiseOrUndefined === undefined) {
                        return;
                    }
                }
                catch (error) {
                    throw error;
                }
                return Promise.resolve(promiseOrUndefined)
                    .then(result => {
                    if (isMounted.current) {
                        dispatch({ type: 'SUBMIT_SUCCESS' });
                    }
                    return result;
                })
                    .catch(_errors => {
                    if (isMounted.current) {
                        dispatch({ type: 'SUBMIT_FAILURE' });
                        // This is a legit error rejected by the onSubmit fn
                        // so we don't want to break the promise chain
                        throw _errors;
                    }
                });
            }
            else if (isMounted.current) {
                // ^^^ Make sure Formalizr is still mounted before updating state
                dispatch({ type: 'SUBMIT_FAILURE' });
                // throw combinedErrors;
                if (isInstanceOfError) {
                    throw combinedErrors;
                }
            }
            return;
        });
    });
    const handleSubmit = useEventCallback((e) => {
        if (e && e.preventDefault && isFunction(e.preventDefault)) {
            e.preventDefault();
        }
        if (e && e.stopPropagation && isFunction(e.stopPropagation)) {
            e.stopPropagation();
        }
        // Warn if form submission is triggered by a <button> without a
        // specified `type` attribute during development. This mitigates
        // a common gotcha in forms with both reset and submit buttons,
        // where the dev forgets to add type="button" to the reset button.
        // if (__DEV__ && typeof document !== 'undefined') {
        //   // Safely get the active element (works with IE)
        //   const activeElement = getActiveElement();
        //   if (
        //     activeElement !== null &&
        //     activeElement instanceof HTMLButtonElement
        //   ) {
        //     warning(
        //       activeElement.attributes &&
        //         activeElement.attributes.getNamedItem('type'),
        //       'You submitted a Formalizr form using a button with an unspecified `type` attribute.  Most browsers default button elements to `type="submit"`. If this is not a submit button, please add `type="button"`.',
        //     );
        //   }
        // }
        submitForm().catch(reason => {
            console.warn(`Warning: An unhandled error was caught from submitForm()`, reason);
        });
    });
    const goToNextStep = useEventCallback(() => {
        return validateFormWithHighPriority().then((combinedErrors) => {
            const isInstanceOfError = combinedErrors instanceof Error;
            const isActuallyValid = !isInstanceOfError && Object.keys(combinedErrors).length === 0;
            if (isActuallyValid) {
                if (isMounted.current && _goToNextStep) {
                    _goToNextStep();
                }
                return;
            }
            else if (isMounted.current) {
                if (isInstanceOfError) {
                    throw combinedErrors;
                }
            }
            return;
        });
    });
    const goToPreviousStep = useEventCallback(() => {
        if (isMounted.current && _goToPreviousStep) {
            _goToPreviousStep();
        }
    });
    const imperativeMethods = {
        resetForm,
        setErrors,
        setFieldError,
        setFieldTouched,
        setFieldValue,
        setFormalizrState,
        setStatus,
        setSubmitting,
        setTouched,
        setValues,
        submitForm,
        // validateField,
        validateForm: validateFormWithHighPriority,
    };
    const executeSubmit = useEventCallback(() => {
        // @ts-expect-error - TODO fix types
        return onSubmit(state.values, imperativeMethods);
    });
    const handleReset = useEventCallback(e => {
        if (e && e.preventDefault && isFunction(e.preventDefault)) {
            e.preventDefault();
        }
        if (e && e.stopPropagation && isFunction(e.stopPropagation)) {
            e.stopPropagation();
        }
        resetForm();
    });
    const getFieldMetaElementLayout = React.useCallback((name) => {
        const element = getUsedDataElements(dataSchema, uiSchema.elements);
        if (element[name]?.layout.widget.options?.i18n &&
            translations !== undefined) {
            const i18nMapping = element[name]?.layout.widget.options?.i18n;
            if (i18nMapping && translations) {
                Object.entries(i18nMapping).forEach(([displayValue, translationKey]) => {
                    const translatedValues = {};
                    Object.entries(translations).forEach(([locale, localeValues]) => {
                        if (localeValues && typeof translationKey === 'string') {
                            const translation = localeValues[translationKey.toString()];
                            if (translation) {
                                translatedValues[locale] = translation;
                            }
                        }
                    });
                    if (Object.keys(translatedValues).length > 0) {
                        i18nMapping[displayValue.toString()] = translatedValues;
                    }
                });
            }
        }
        return element[name]?.layout;
    }, [dataSchema, uiSchema.elements, translations]);
    const getFieldData = React.useCallback((name) => {
        const element = getUsedDataElements(dataSchema, uiSchema.elements);
        const data = element[name]?.data;
        return {
            maximum: data?.maximum,
            minimum: data?.minimum,
        };
    }, [dataSchema, uiSchema.elements]);
    const shouldShowErrorMessage = React.useCallback((name) => {
        if (disableButtonsOnErrors) {
            return getIn(state.errors, name) ? false : true;
        }
        return true;
    }, [disableButtonsOnErrors, state.errors]);
    const getFieldMeta = React.useCallback((name) => {
        return {
            error: getIn(state.errors, name),
            initialError: getIn(initialErrors.current, name),
            initialTouched: !!getIn(initialTouched.current, name),
            initialValue: getIn(initialValues.current, name),
            scope: getFieldMetaElementLayout(name)?.scope,
            showErrorMessage: shouldShowErrorMessage(name),
            touched: !!getIn(state.touched, name),
            value: getIn(state.values, name),
            widget: getFieldMetaElementLayout(name)?.widget,
            ...getFieldData(name),
        };
    }, [
        state.errors,
        state.touched,
        state.values,
        getFieldMetaElementLayout,
        shouldShowErrorMessage,
        getFieldData,
    ]);
    const getFieldHelpers = React.useCallback((name) => {
        return {
            setError: (value) => setFieldError(name, value),
            setTouched: (value, shouldValidate) => setFieldTouched(name, value, shouldValidate),
            setValue: (value, shouldValidate) => setFieldValue(name, value, shouldValidate),
        };
    }, [setFieldValue, setFieldTouched, setFieldError]);
    const getFieldProps = React.useCallback((nameOrOptions) => {
        const isAnObject = isObject(nameOrOptions);
        const name = isAnObject
            ? nameOrOptions.name
            : nameOrOptions;
        const valueState = getIn(state.values, name);
        // Create a proper label object for translations
        const label = translations[locale]?.[name]
            ? { [locale]: translations[locale]?.[name] ?? '' }
            : {};
        const field = {
            label,
            locale,
            name,
            onBlur: handleBlur,
            onChange: handleChange,
            value: valueState,
        };
        if (isAnObject) {
            const { type, value: valueProp, // value is special for checkboxes
            as: is, multiple, } = nameOrOptions;
            if (type === 'checkbox') {
                if (valueProp === undefined) {
                    field.checked = !!valueState;
                }
                else {
                    field.checked = !!(Array.isArray(valueState) && ~valueState.indexOf(valueProp));
                    field.value = valueProp;
                }
            }
            else if (type === 'radio') {
                field.checked = valueState === valueProp;
                field.value = valueProp;
            }
            else if (is === 'select' && multiple) {
                field.value = field.value || [];
                field.multiple = true;
            }
        }
        return field;
    }, [handleBlur, state.values]);
    const dirty = React.useMemo(() => !isEqual(initialValues.current, state.values), [initialValues.current, state.values]);
    // const isValid = React.useMemo(
    //   () =>
    //     typeof isInitialValid !== 'undefined'
    //       ? dirty
    //         ? state.errors && Object.keys(state.errors).length === 0
    //         : isInitialValid !== false && isFunction(isInitialValid)
    //           ? (isInitialValid as (props: FormalizrConfig<Values>) => boolean)(
    //               props,
    //             )
    //           : (isInitialValid as boolean)
    //       : state.errors && Object.keys(state.errors).length === 0,
    //   [isInitialValid, dirty, state.errors, props],
    // );
    return {
        ...state,
        currentStep,
        dataSchema,
        dirty,
        getFieldHelpers,
        getFieldMeta,
        getFieldProps,
        goToNextStep,
        goToPreviousStep,
        handleBlur,
        // handleChange,
        handleReset,
        handleSubmit,
        initialErrors: initialErrors.current,
        initialStatus: initialStatus.current,
        initialTouched: initialTouched.current,
        initialValues: initialValues.current,
        locale,
        numberOfSteps,
        // isValid,
        registerField,
        resetForm,
        setErrors,
        setFieldError,
        setFieldTouched,
        setFieldValue,
        setFormalizrState,
        setStatus,
        setSubmitting,
        setTouched,
        setValues,
        submitForm,
        translations,
        // uiSchema: data ? data : state.uiSchema,
        unregisterField,
        // validateField,
        validateForm: validateFormWithHighPriority,
        validateOnBlur,
        validateOnChange,
        validateOnMount,
    };
};
/**
 * deepmerge array merging algorithm
 * https://github.com/KyleAMathews/deepmerge#combine-array
 */
function arrayMerge(target, source, options) {
    const destination = target.slice();
    source.forEach(function merge(e, i) {
        if (typeof destination[i] === 'undefined') {
            const cloneRequested = options.clone !== false;
            const shouldClone = cloneRequested && options.isMergeableObject(e);
            destination[i] = shouldClone
                ? // @ts-expect-error - TODO fix types
                    deepmerge(Array.isArray(e) ? [] : {}, e, options)
                : e;
        }
        else if (options.isMergeableObject(e)) {
            destination[i] = deepmerge(target[i], e, options);
        }
        else if (target.indexOf(e) === -1) {
            destination.push(e);
        }
    });
    return destination;
}
/**
 * Transform Yup ValidationError to a more usable object
 */
function yupToFormErrors(yupError) {
    let errors = {};
    if (yupError.inner) {
        if (yupError.inner.length === 0) {
            return setIn(errors, yupError.path, yupError.message);
        }
        for (const err of yupError.inner) {
            if (!getIn(errors, err.path)) {
                // errors = setIn(errors, err.path, err.message);
                errors = {
                    ...errors,
                    [err.path]: err.message,
                };
            }
        }
    }
    return errors;
}
/**
 * Validate a yup schema.
 */
function validateYupSchema(values, schema, sync = false, context) {
    const normalizedValues = prepareDataForValidation(values);
    const validation = schema[sync ? 'validateSync' : 'validate'](normalizedValues, {
        abortEarly: false,
        context: context || normalizedValues,
    });
    return validation;
}
/**
 * Recursively prepare values.
 */
function prepareDataForValidation(values) {
    const data = Array.isArray(values) ? [] : {};
    for (const k in values) {
        if (Object.prototype.hasOwnProperty.call(values, k)) {
            const key = String(k);
            if (Array.isArray(values[key]) === true) {
                data[key] = values[key].map((value) => {
                    if (Array.isArray(value) === true || isPlainObject(value)) {
                        return prepareDataForValidation(value);
                    }
                    else {
                        return value !== '' ? value : undefined;
                    }
                });
            }
            else if (isPlainObject(values[key])) {
                data[key] = prepareDataForValidation(values[key]);
            }
            else {
                data[key] = values[key] !== '' ? values[key] : undefined;
            }
        }
    }
    return data;
}
function warnAboutMissingIdentifier({ htmlContent, documentationAnchorLink, handlerName, }) {
    console.warn(`Warning: Formik called \`${handlerName}\`, but you forgot to pass an \`id\` or \`name\` attribute to your input:
    ${htmlContent}
    Formik cannot determine which value to update. For more info see https://formik.org/docs/api/formik#${documentationAnchorLink}
  `);
}
/** Return multi select values based on an array of options */
function getSelectedValues(options) {
    return Array.from(options)
        .filter(el => el.selected)
        .map(el => el.value);
}
/** Return the next value for a checkbox */
function getValueForCheckbox(currentValue, checked, valueProp) {
    // If the current value was a boolean, return a boolean
    if (typeof currentValue === 'boolean') {
        return Boolean(checked);
    }
    // If the currentValue was not a boolean we want to return an array
    let currentArrayOfValues = [];
    let isValueInArray = false;
    let index = -1;
    if (!Array.isArray(currentValue)) {
        // eslint-disable-next-line eqeqeq
        if (!valueProp || valueProp == 'true' || valueProp == 'false') {
            return Boolean(checked);
        }
    }
    else {
        // If the current value is already an array, use it
        // @ts-expect-error - TODO fix types
        currentArrayOfValues = currentValue;
        index = currentValue.indexOf(valueProp);
        isValueInArray = index >= 0;
    }
    // If the checkbox was checked and the value is not already present in the aray we want to add the new value to the array of values
    if (checked && valueProp && !isValueInArray) {
        return currentArrayOfValues.concat(valueProp);
    }
    // If the checkbox was unchecked and the value is not in the array, simply return the already existing array of values
    if (!isValueInArray) {
        return currentArrayOfValues;
    }
    // If the checkbox was unchecked and the value is in the array, remove the value and return the array
    return currentArrayOfValues
        .slice(0, index)
        .concat(currentArrayOfValues.slice(index + 1));
}

const Buttons = ({ componentMap, uiSchema, onNext, onPrevious, currentStep, totalSteps, errors, disableButtonsOnErrors, }) => {
    if (uiSchema?.type === 'Category') {
        const NextButtonComponent = componentMap['NextButton'];
        const nextButton = uiSchema.options?.nextButton;
        const showNextButton = nextButton?.display;
        const nextButtonLabel = nextButton?.i18n || nextButton?.label;
        const nextButtonReference = nextButton?.reference;
        const PreviousButtonComponent = componentMap['PreviousButton'];
        const previousButton = uiSchema.options?.previousButton;
        const showPreviousButton = previousButton?.display;
        const previousButtonLabel = previousButton?.i18n || previousButton?.label;
        const previousButtonReference = previousButton?.reference;
        const SubmitButtonComponent = componentMap['SubmitButton'];
        const ButtonWrapperComponent = componentMap['ButtonWrapper'] ?? React__default.Fragment;
        return (jsxs(ButtonWrapperComponent, { children: [showPreviousButton && PreviousButtonComponent && (jsx(PreviousButtonComponent, { type: "button", onClick: onPrevious, reference: previousButtonReference, children: previousButtonLabel })), showNextButton &&
                    currentStep !== totalSteps &&
                    NextButtonComponent && (jsx(NextButtonComponent, { type: "button", disabled: !isEmpty(errors) && disableButtonsOnErrors, onClick: onNext, reference: nextButtonReference, children: nextButtonLabel })), currentStep === totalSteps && SubmitButtonComponent && (jsx(SubmitButtonComponent, { type: "submit", disabled: !isEmpty(errors) && disableButtonsOnErrors, reference: nextButtonReference, children: nextButtonLabel }))] }));
    }
    const SubmitButtonComponent = componentMap['SubmitButton'];
    const submitButton = uiSchema.options?.submitButton;
    const showSubmitButton = submitButton?.display;
    const submitButtonLabel = submitButton?.i18n || submitButton?.label;
    const submitButtonReference = submitButton?.reference;
    const ResetButtonComponent = componentMap['ResetButton'];
    const resetButton = uiSchema.options?.resetButton;
    const showResetButton = resetButton?.display;
    const resetButtonLabel = resetButton?.i18n || resetButton?.label;
    const resetButtonReference = resetButton?.reference;
    const ButtonWrapperComponent = componentMap['ButtonWrapper'] ?? React__default.Fragment;
    return (jsxs(ButtonWrapperComponent, { children: [showResetButton && ResetButtonComponent && (jsx(ResetButtonComponent, { type: "reset", reference: resetButtonReference, children: resetButtonLabel })), showSubmitButton && SubmitButtonComponent && (jsx(SubmitButtonComponent, { type: "submit", disabled: !isEmpty(errors) && disableButtonsOnErrors, reference: submitButtonReference, children: submitButtonLabel }))] }));
};

const Form = React__default.forwardRef((props, ref) => {
    // iOS needs an "action" attribute for nice input: https://stackoverflow.com/a/39485162/406725
    // We default the action to "#" in case the preventDefault fails (just updates the URL hash)
    const { children } = props;
    const context = useFormalizrContext();
    return (jsx("form", { ref: ref, onReset: context.handleReset, onSubmit: context.handleSubmit, children: children }));
});
Form.displayName = 'Form';

const renderElements = (layout, componentMap, data, translations = {}, errors, disableButtonsOnErrors, handleNext, handlePrevious, currentStep, numberOfSteps) => {
    const components = layout.map((layoutElement, index) => {
        if (layoutElement.type === 'Categorization') {
            const Component = layoutElement?.options?.variant === 'stepper'
                ? componentMap['CategorizationStepper']
                : componentMap['CategorizationTabs'];
            return (jsx(Component, { currentStep, numberOfSteps, ...layoutElement, children: renderElements(layoutElement.elements, componentMap, data, translations, errors, disableButtonsOnErrors, handleNext, handlePrevious, currentStep, numberOfSteps) }, index));
        }
        if (layoutElement.type === 'Category') {
            const Component = componentMap['Category'];
            return (jsxs(Component, { ...layoutElement, children: [renderElements(layoutElement.elements, componentMap, data, translations, errors, disableButtonsOnErrors), handleNext && handlePrevious && (jsx(Buttons, { componentMap: componentMap, uiSchema: layoutElement, onNext: handleNext, onPrevious: handlePrevious, currentStep: currentStep, totalSteps: numberOfSteps, errors: errors, disableButtonsOnErrors: disableButtonsOnErrors }))] }, index));
        }
        if (layoutElement.type === 'ContentfulEntry') {
            const Component = componentMap['ContentfulEntry'];
            if (!Component)
                return null;
            return jsx(Component, { ...layoutElement.reference }, index);
        }
        if (layoutElement.type === 'VerticalLayout') {
            const Component = componentMap['VerticalLayout'];
            return (jsx(Component, { ...layoutElement, children: renderElements(layoutElement.elements, componentMap, data, translations, errors, disableButtonsOnErrors) }, index));
        }
        if (layoutElement.type === 'HorizontalLayout') {
            const Component = componentMap['HorizontalLayout'];
            return (jsx(Component, { ...layoutElement, children: renderElements(layoutElement.elements, componentMap, data, translations, errors, disableButtonsOnErrors) }, index));
        }
        if (layoutElement.type === 'Group') {
            const Component = componentMap['Group'];
            return (jsx(Component, { ...layoutElement, children: renderElements(layoutElement.elements, componentMap, data, translations, errors, disableButtonsOnErrors) }, index));
        }
        if (layoutElement.type === 'Control') {
            const Component = componentMap[layoutElement?.widget?.type];
            const lastIndex = layoutElement.scope?.lastIndexOf('/');
            const name = layoutElement.scope?.slice(lastIndex + 1);
            if (Component) {
                return (jsx(Component, { name: name, ...layoutElement?.widget?.options }, index));
            }
            return null;
        }
    });
    return components;
};
const Formalizr = (props) => {
    // @ts-expect-error - TODO types have to be fixed
    const propsContext = useFormalizr(props);
    const handleNext = (e) => {
        e.preventDefault();
        if (propsContext.goToNextStep) {
            propsContext.goToNextStep();
        }
    };
    const handlePrevious = (e) => {
        e.preventDefault();
        if (propsContext.goToPreviousStep) {
            propsContext.goToPreviousStep();
        }
    };
    const elements = renderElements(propsContext.uiSchemaCurrent.elements, props.componentMap, props.schema.output.data, props.schema.output.translations, propsContext.errors, props.disableButtonsOnErrors, handleNext, handlePrevious, propsContext.currentStep, propsContext.numberOfSteps);
    return (
    //@ts-expect-error - TODO types have to be fixed
    jsx(FormalizrProvider, { value: propsContext, children: jsxs(Form, { ref: props.innerRef, ...props, children: [elements, propsContext.numberOfSteps === 1 && (jsx(Buttons, { componentMap: props.componentMap, uiSchema: propsContext.uiSchema, onNext: handleNext, onPrevious: handlePrevious, currentStep: propsContext.currentStep, totalSteps: propsContext.numberOfSteps, errors: propsContext.errors, disableButtonsOnErrors: props.disableButtonsOnErrors }))] }) }));
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
function useField(props) {
    const formalizr = useFormalizrContext();
    const { getFieldProps, getFieldMeta, getFieldHelpers, registerField, unregisterField, locale, } = formalizr;
    const { name: fieldName, validate: validateFn } = props;
    React.useEffect(() => {
        if (fieldName) {
            registerField(fieldName, {
                validate: validateFn,
            });
        }
        return () => {
            if (fieldName) {
                unregisterField(fieldName);
            }
        };
    }, [fieldName, registerField, unregisterField, validateFn]);
    warning(formalizr, 'Formalizr is undefined, Please check if you are calling useField() as a child of a <Formalizr /> component');
    warning(fieldName, 'Invalid field name. Pass `useField` an object containing a `name` key.');
    const fieldHelpers = React.useMemo(() => getFieldHelpers(fieldName), [getFieldHelpers, fieldName]);
    return [getFieldProps(props), getFieldMeta(fieldName), locale, fieldHelpers];
}

export { Formalizr, FormalizrConsumer, FormalizrContext, FormalizrProvider, useField, useFormalizrContext };
